import type { BacklinksDTO } from './auth/apiClient.js';

/**
 * A tiny localStorage cache of the FIRST page of Linked References per note, so re-opening a page
 * paints its backlinks instantly (store-and-hydrate) instead of flashing empty while the network
 * round-trips. Only the default view is cached — the unfiltered, offset-0 slice — so searches and
 * "show more" always hit the server. The component still refetches in the background and replaces
 * the cached value, so a stale cache is only ever shown for a few hundred milliseconds.
 *
 * Bounded to {@link MAX_ENTRIES} notes with simple LRU eviction; writes are best-effort and every
 * access is wrapped so a disabled/full localStorage (private mode, quota) degrades to no cache.
 */
const PREFIX = 'lapo:blcache:';
const INDEX_KEY = 'lapo:blcache:index';
const MAX_ENTRIES = 60;

function store(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null; // access itself can throw when cookies/storage are blocked
  }
}

function readIndex(s: Storage): string[] {
  try {
    const raw = s.getItem(INDEX_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? (arr as string[]) : [];
  } catch {
    return [];
  }
}

/** The cached first page for a note, or null if nothing is cached (or storage is unavailable). */
export function readBacklinksCache(noteId: string): BacklinksDTO | null {
  const s = store();
  if (!s) return null;
  try {
    const raw = s.getItem(PREFIX + noteId);
    if (!raw) return null;
    const data = JSON.parse(raw) as BacklinksDTO;
    return data && Array.isArray(data.groups) ? data : null;
  } catch {
    return null;
  }
}

/** Cache the default first page for a note and mark it most-recently-used, evicting the oldest. */
export function writeBacklinksCache(noteId: string, data: BacklinksDTO): void {
  const s = store();
  if (!s) return;
  try {
    s.setItem(PREFIX + noteId, JSON.stringify(data));
    const index = readIndex(s).filter((id) => id !== noteId);
    index.push(noteId);
    while (index.length > MAX_ENTRIES) {
      const evicted = index.shift();
      if (evicted) s.removeItem(PREFIX + evicted);
    }
    s.setItem(INDEX_KEY, JSON.stringify(index));
  } catch {
    // Quota or serialization failure — the cache is an optimization, so just skip it.
  }
}
